<template>
  <div class="profile-step">
    <div class="auth-tabs">
      <div class="auth-tab" @click="goToLogin">LOGIN</div>
      <div class="auth-tab active">REGISTER</div>
    </div>
    
    <div class="profile-content">
      <!-- General error banner -->
      <div v-if="generalError" class="error-message general-error">{{ generalError }}</div>
      
      <!-- Profile Picture -->
      <div class="profile-picture-section">
        <div class="profile-picture">
          <img v-if="previewImage" :src="previewImage" alt="Profile picture">
          <div v-else class="profile-placeholder">
            <span>{{ getInitials() }}</span>
          </div>
        </div>
        <button class="btn-change-picture" @click="triggerFileInput">
          <v-icon size="small">mdi-plus</v-icon> Change profile picture
        </button>
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          accept="image/*"
          @change="onFileSelected"
        >
      </div>
      
      <!-- Image validation error -->
      <div v-if="errors.image" class="error-message image-error">{{ errors.image }}</div>
      
      <!-- Profile Form -->
      <div class="form-group">
        <label for="firstName">First name</label>
        <input 
          type="text"
          id="firstName"
          v-model="profileData.firstName"
          class="form-control"
          placeholder="Jane"
          required
        >
        <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
      </div>
      
      <div class="form-group">
        <label for="lastName">Last name</label>
        <input 
          type="text"
          id="lastName"
          v-model="profileData.lastName"
          class="form-control"
          placeholder="Doe"
          required
        >
        <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
      </div>
      
      <div class="form-group">
        <label for="email">Email</label>
        <input 
          type="email"
          id="email"
          v-model="profileData.email"
          class="form-control"
          placeholder="email@example.com"
          required
        >
        <div v-if="errors.email" class="error-message">{{ errors.email }}</div>
      </div>
      
      <div class="form-group">
        <label for="phone">Phone</label>
        <input 
          type="tel"
          id="phone"
          v-model="profileData.phone"
          class="form-control"
          placeholder="000 0000 0000"
          required
        >
        <div v-if="errors.phone" class="error-message">{{ errors.phone }}</div>
      </div>
      
      <div class="form-group">
        <label for="linkedinProfileUrl">LinkedIn Profile URL (Optional)</label>
        <input 
          type="url"
          id="linkedinProfileUrl"
          v-model="profileData.linkedinProfileUrl"
          class="form-control"
          placeholder="https://linkedin.com/in/your-profile"
        >
        <div v-if="errors.linkedinProfileUrl" class="error-message">{{ errors.linkedinProfileUrl }}</div>
      </div>
      
      <div class="form-group">
        <label for="currentCompany">Company you work for</label>
        <input 
          type="text"
          id="currentCompany"
          v-model="profileData.currentCompany"
          class="form-control"
          placeholder="Company name"
          required
        >
        <div v-if="errors.currentCompany" class="error-message">{{ errors.currentCompany }}</div>
      </div>
      
      <!-- Gender Selection -->
      <div class="form-group">
        <label class="gender-label">Gender</label>
        <div class="gender-options">
          <label class="gender-option">
            <input 
              type="radio" 
              name="gender" 
              value="male"
              v-model="profileData.gender"
            >
            <span class="radio-circle"></span>
            Male
          </label>
          
          <label class="gender-option">
            <input 
              type="radio" 
              name="gender" 
              value="female"
              v-model="profileData.gender"
            >
            <span class="radio-circle"></span>
            Female
          </label>
          
          <label class="gender-option">
            <input 
              type="radio" 
              name="gender" 
              value="other"
              v-model="profileData.gender"
            >
            <span class="radio-circle"></span>
            Other
          </label>
        </div>
        <div v-if="errors.gender" class="error-message">{{ errors.gender }}</div>
      </div>
      
      <!-- Continue Button -->
      <button 
        class="btn btn-continue"
        @click="handleContinue"
        :disabled="isSubmitting"
      >
        <template v-if="isSubmitting">
          <v-progress-circular indeterminate color="white" size="20" class="spinner" />
        </template>
        <template v-else>
          Continue <v-icon>mdi-arrow-right</v-icon>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterStep4',
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      profileData: {
        firstName: this.formData.firstName || '',
        lastName: this.formData.lastName || '',
        email: this.formData.email || '',
        phone: this.formData.mobilePhone || '',
        gender: this.formData.gender || null,
        linkedinProfileUrl: this.formData.linkedinProfileUrl || '',
        currentCompany: this.formData.currentCompany || ''
      },
      previewImage: null,
      selectedFile: null,
      isSubmitting: false,
      generalError: '',
      errors: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: '',
        image: '',
        linkedinProfileUrl: '',
        currentCompany: ''
      }
    };
  },
  methods: {
    goToLogin() {
      this.$router.push({ name: 'login' });
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          this.errors.image = 'Please select a PNG, JPEG, or JPG image file.';
          // Reset the file input
          event.target.value = '';
          return;
        }
        
        // Validate file size (2MB = 2 * 1024 * 1024 bytes)
        const maxSize = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > maxSize) {
          this.errors.image = 'Image size must be less than 2MB.';
          // Reset the file input
          event.target.value = '';
          return;
        }
        
        // Clear any previous image errors
        this.errors.image = '';
        
        this.selectedFile = file;
        this.previewImage = URL.createObjectURL(file);
      }
    },
    validateForm() {
      let isValid = true;
      this.generalError = '';
      this.errors = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: '',
        image: '',
        linkedinProfileUrl: '',
        currentCompany: ''
      };
      
      // First name validation
      if (!this.profileData.firstName.trim()) {
        this.errors.firstName = 'First name is required';
        isValid = false;
      }

      // Last name validation
      if (!this.profileData.lastName.trim()) {
        this.errors.lastName = 'Last name is required';
        isValid = false;
      }
      
      // Email validation
      if (!this.profileData.email.trim()) {
        this.errors.email = 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.profileData.email)) {
        this.errors.email = 'Please enter a valid email address';
        isValid = false;
      }
      
      // Phone validation
      if (!this.profileData.phone.trim()) {
        this.errors.phone = 'Phone number is required';
        isValid = false;
      }
      
      // LinkedIn Profile URL validation (optional)
      if (this.profileData.linkedinProfileUrl && !/^https?:\/\/.+/.test(this.profileData.linkedinProfileUrl)) {
        this.errors.linkedinProfileUrl = 'Please enter a valid URL (e.g., https://linkedin.com/in/your-profile)';
        isValid = false;
      } else {
        this.errors.linkedinProfileUrl = ''; // Clear error if valid
      }
      
      // Current company validation (required)
      if (!this.profileData.currentCompany.trim()) {
        this.errors.currentCompany = 'Company you work for is required';
        isValid = false;
      }
      
      // Gender validation
      if (!this.profileData.gender) {
        this.errors.gender = 'Please select a gender';
        isValid = false;
      }
      
      if (!isValid) {
        this.generalError = 'Please correct the highlighted errors.';
      }
      return isValid;
    },
    handleContinue() {
      if (this.validateForm()) {
        this.isSubmitting = true;
        
        const updatedData = {
          ...this.formData,
          firstName: this.profileData.firstName,
          lastName: this.profileData.lastName,
          email: this.profileData.email,
          mobilePhone: this.profileData.phone,
          gender: this.profileData.gender,
          profilePicture: this.selectedFile,
          linkedinProfileUrl: this.profileData.linkedinProfileUrl,
          currentCompany: this.profileData.currentCompany
        };
        
        // Emit updated form data
        this.$emit('update:form-data', updatedData);
        
        // Move to next step
        this.$emit('next-step');
        
        this.isSubmitting = false;
      }
    },
    getInitials() {
      const firstName = this.profileData.firstName || '';
      const lastName = this.profileData.lastName || '';
      const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      return initials || 'U'; // Default to 'U' for User if no names entered
    }
  }
};
</script>

<style scoped>
.profile-step {
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.auth-tabs {
  display: flex;
  width: 100%;
}

.auth-tab {
  flex: 1;
  padding: 15px;
  text-align: center;
  background-color: #f1f5f9;
  color: #333;
  font-weight: bold;
  cursor: pointer;
}

.auth-tab.active {
  background-color: white;
}

.profile-content {
  padding: 20px 30px 30px;
}

.profile-picture-section {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
}

.profile-picture {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.profile-picture img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ccc;
  color: white;
  font-weight: bold;
}

.btn-change-picture {
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 15px;
  font-size: 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.btn-change-picture:hover {
  background-color: #f5f5f5;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.gender-label {
  margin-bottom: 15px;
}

.gender-options {
  display: flex;
  gap: 30px;
}

.gender-option {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.gender-option input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.radio-circle {
  position: relative;
  height: 20px;
  width: 20px;
  background-color: #f0f0f0;
  border-radius: 50%;
  margin-right: 10px;
  display: inline-block;
  border: 1px solid #ddd;
}

.gender-option input:checked ~ .radio-circle {
  background-color: white;
  border: 2px solid #e94057;
}

.gender-option input:checked ~ .radio-circle:after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e94057;
}

.error-message {
  color: #e94057;
  font-size: 12px;
  margin-top: 5px;
}

.image-error {
  margin-bottom: 20px;
  text-align: center;
}

.btn-continue {
  width: 100%;
  padding: 15px;
  background-color: #e94057;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-continue:hover {
  background-color: #d03a4e;
}

.btn-continue:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.general-error {
  text-align: center;
}

.spinner {
  margin: 0;
}
</style> 